import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const CarrotCatchGameTest = ({ verseId, onClose }) => {
  const canvasRef = useRef(null);
  const basketRef = useRef({ x: 137, y: 450, width: 100, height: 60 }); // Use ref for smooth movement
  const lastSpawnTimeRef = useRef(0); // Track last spawn time to prevent too-frequent spawning
  const spawnQueueRef = useRef([]); // Pre-generated queue of items to spawn
  const spawnQueueIndexRef = useRef(0); // Track position in spawn queue
  const correctWordSpawnCountRef = useRef(0); // Track spawns since last correct word
  const basketImageRef = useRef(null); // Pre-loaded basket image
  const carrotImageRef = useRef(null); // Pre-loaded carrot image
  const [gameState, setGameState] = useState('loading');
  const [verse, setVerse] = useState(null);
  const [carrots, setCarrots] = useState([]);
  const [sequence, setSequence] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [score, setScore] = useState(0);
  const [caughtWords, setCaughtWords] = useState([]); // Track caught words for display
  const { toast } = useToast();

  const CANVAS_WIDTH = 375; // Phone screen width
  const CANVAS_HEIGHT = 600; // Phone screen height
  const CARROT_FALL_TIME = 1500; // 1.5 seconds
  const MAX_CARROTS_ON_SCREEN = 4; // 3-4 carrots visible at once (reduced to prevent overlap)
  const CARROT_SPAWN_INTERVAL = 900; // Spawn every 0.9 seconds

  // Pre-load images on component mount
  useEffect(() => {
    const basketImg = new Image();
    basketImg.src = '/carrot_basket.png';
    basketImg.onload = () => {
      basketImageRef.current = basketImg;
    };
    
    const carrotImg = new Image();
    carrotImg.src = '/carrot_mg.png';
    carrotImg.onload = () => {
      carrotImageRef.current = carrotImg;
    };
  }, []);

  useEffect(() => {
    if (verseId) {
      loadVerse(verseId);
    }
  }, []);

  const loadVerse = async (verseId) => {
    try {
      const response = await api.post('/minigame/test/verse', {
        verse_id: verseId,
        bible_version: 'd6e14a625393b4da-01'
      });
      
      const verseData = response.data;
      const words = verseData.text.split(/\s+/).filter(word => word.length > 0);
      
      const gameSequence = [
        { text: verseData.reference, type: 'reference', id: 'ref-start' },
        ...words.map((word, idx) => ({ text: word, type: 'word', id: `word-${idx}` })),
        { text: verseData.reference, type: 'reference', id: 'ref-end' }
      ];
      
      setVerse(verseData);
      setSequence(gameSequence);
      setGameState('playing');
      startGame(gameSequence);
    } catch (error) {
      console.error('Test verse loading error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to load test verse",
        variant: "destructive"
      });
      setGameState('error');
    }
  };

  const startGame = (seq) => {
    // PRE-GENERATE SPAWN QUEUE: Create a randomized list of all items to spawn
    // This eliminates the need to filter/search during game loop
    const spawnQueue = [];
    
    // Helper: Check if a word text appears earlier in the sequence (to prevent duplicate confusion)
    const hasEarlierDuplicate = (item, maxIndex) => {
      for (let i = 0; i < maxIndex; i++) {
        if (seq[i].text === item.text && seq[i].id !== item.id) {
          return true;
        }
      }
      return false;
    };
    
    // Strategy: Ensure correct word appears every 2-3 spawns, fill rest with random words
    // IMPORTANT: Don't spawn duplicate words (same text) until their earlier instances are caught
    let currentCorrectIndex = 0;
    let spawnsSinceLastCorrect = 0;
    const totalSpawns = seq.length * 4; // Spawn approximately 4x the number of words needed for buffer
    
    for (let i = 0; i < totalSpawns; i++) {
      // Every 2-3 spawns, add the next correct word
      if (spawnsSinceLastCorrect >= 2 && currentCorrectIndex < seq.length) {
        spawnQueue.push(seq[currentCorrectIndex]);
        currentCorrectIndex++;
        spawnsSinceLastCorrect = 0;
      } else {
        // Add a random word, but ONLY if it doesn't have an earlier duplicate
        // that hasn't been caught yet (based on currentCorrectIndex)
        let attempts = 0;
        let randomItem;
        
        do {
          randomItem = seq[Math.floor(Math.random() * seq.length)];
          attempts++;
          
          // Get the index of this random item in the sequence
          const randomItemIndex = seq.findIndex(item => item.id === randomItem.id);
          
          // Don't allow spawning items that come AFTER the current progress
          // AND have the same text as an earlier item (prevents ref-end spawning before ref-start)
          if (randomItemIndex >= currentCorrectIndex) {
            // This item is at or after our current progress
            // Check if there's an earlier duplicate ANYWHERE before this item's position
            if (!hasEarlierDuplicate(randomItem, randomItemIndex)) {
              break; // Safe to use - no earlier duplicate in entire sequence before this position
            }
            // Has earlier duplicate - try again
          } else {
            // This item is before current progress, already should have been caught
            // Only spawn if no earlier duplicate exists
            if (!hasEarlierDuplicate(randomItem, randomItemIndex)) {
              break; // Safe to use
            }
          }
          
          // If we've tried 10 times, just use the current correct word to be safe
          if (attempts > 10) {
            randomItem = seq[Math.min(currentCorrectIndex, seq.length - 1)];
            break;
          }
        } while (attempts < 10);
        
        spawnQueue.push(randomItem);
        spawnsSinceLastCorrect++;
      }
    }
    
    // Save to ref so it persists and doesn't cause re-renders
    spawnQueueRef.current = spawnQueue;
    spawnQueueIndexRef.current = 0;
    
    // Start with just 1 carrot - let spawning handle the rest
    const initialCarrots = [];
    if (spawnQueue.length > 0) {
      initialCarrots.push(createCarrot(spawnQueue[0], 0, []));
      spawnQueueIndexRef.current = 1;
    }
    
    setCarrots(initialCarrots);
    setCaughtWords([]); // Reset caught words display
  };

  const findSafeSpawnPosition = (existingCarrots) => {
    const carrotWidth = 63; // Updated for 1.25x size
    const minHorizontalGap = 80; // Increased gap for bigger carrots
    const minVerticalGap = 180; // MUCH larger vertical gap to prevent any stacking
    
    let attempts = 0;
    const maxAttempts = 50; // Reduced from 150 for better performance
    
    while (attempts < maxAttempts) {
      const x = Math.random() * (CANVAS_WIDTH - carrotWidth - 60) + 30;
      const y = -70;
      
      // Check if this position overlaps with any existing carrot
      const hasOverlap = existingCarrots.some(carrot => {
        // Check current position
        const currentHorizontalOverlap = Math.abs(carrot.x - x) < minHorizontalGap;
        const currentVerticalOverlap = Math.abs(carrot.y - y) < minVerticalGap;
        
        // Calculate where carrot will be when new carrot reaches that position
        const timeToFall = Math.abs(carrot.y - y) / carrot.speed;
        const futureY = carrot.y + (carrot.speed * timeToFall);
        const futureVerticalOverlap = Math.abs(futureY - y) < minVerticalGap;
        
        // Also check if carrot is within spawn zone (recently spawned)
        const inSpawnZone = carrot.y < 100;
        
        // Return true if there's any overlap or too close in spawn zone
        return (currentHorizontalOverlap && (currentVerticalOverlap || inSpawnZone)) || 
               (currentHorizontalOverlap && futureVerticalOverlap);
      });
      
      if (!hasOverlap) {
        return x;
      }
      
      attempts++;
    }
    
    // If no safe position found, force minimum spacing from center
    return Math.random() * (CANVAS_WIDTH - carrotWidth - 60) + 30;
  };

  const createCarrot = (item, delay = 0, existingCarrots = []) => {
    // Calculate speed so carrot falls from top (-70) to bottom (CANVAS_HEIGHT) in exactly 4 seconds
    const totalDistance = CANVAS_HEIGHT + 70;
    const framesIn4Seconds = (CARROT_FALL_TIME / 1000) * 60; // 60 FPS
    const speedPerFrame = totalDistance / framesIn4Seconds;
    
    // Find a safe spawn position that doesn't overlap with existing carrots
    const safeX = findSafeSpawnPosition(existingCarrots);
    
    return {
      id: `${item.id}-${Date.now()}-${Math.random()}`,
      text: item.text,
      type: item.type,
      sequenceId: item.id,
      x: safeX,
      y: -70,
      width: 63,  // 1.25x bigger (50 * 1.25 = 62.5, rounded to 63)
      height: 88, // 1.25x bigger (70 * 1.25 = 87.5, rounded to 88)
      speed: speedPerFrame,
      caught: false,
      spawnTime: Date.now() + delay
    };
  };

  // Use refs to avoid re-creating game loop on every state change
  const currentIndexRef = useRef(0);
  const sequenceRef = useRef([]);
  
  // Sync refs with state
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);
  
  useEffect(() => {
    sequenceRef.current = sequence;
  }, [sequence]);

  useEffect(() => {
    if (gameState !== 'playing') return;

    let animationId;
    let lastTime = Date.now();
    
    const gameLoop = () => {
      const now = Date.now();
      const deltaTime = now - lastTime;
      lastTime = now;
      
      setCarrots(prevCarrots => {
        // 1. CLEAN: Remove caught and off-screen carrots (aggressive cleanup for performance)
        let updatedCarrots = prevCarrots.filter(c => !c.caught && c.y < CANVAS_HEIGHT + 20);
        
        // 2. UPDATE POSITIONS: Move carrots down
        updatedCarrots = updatedCarrots.map(carrot => {
          if (now < carrot.spawnTime) return carrot;
          return { ...carrot, y: carrot.y + carrot.speed };
        });

        // 3. CHECK COLLISIONS
        const basket = basketRef.current;
        const currentSeq = sequenceRef.current;
        const currentIdx = currentIndexRef.current;
        
        for (let i = 0; i < updatedCarrots.length; i++) {
          const carrot = updatedCarrots[i];
          if (now < carrot.spawnTime) continue;

          const carrotBottom = carrot.y + carrot.height;
          const carrotCenterX = carrot.x + carrot.width / 2;

          if (
            carrotBottom >= basket.y &&
            carrotBottom <= basket.y + basket.height &&
            carrotCenterX >= basket.x &&
            carrotCenterX <= basket.x + basket.width
          ) {
            const expectedItem = currentSeq[currentIdx];
            
            if (carrot.sequenceId === expectedItem.id) {
              updatedCarrots.splice(i, 1);
              setScore(prev => prev + 10);
              setCurrentIndex(prev => prev + 1);
              setCaughtWords(prev => [...prev, expectedItem.text]); // Add to caught words display
              
              if (currentIdx + 1 >= currentSeq.length) {
                setGameState('won');
              }
              break;
            } else {
              setMistakes(prev => prev + 1);
              toast({
                title: "Wrong Word!",
                description: `You caught "${carrot.text}" but needed "${expectedItem.text}"`,
                variant: "destructive"
              });
              
              setTimeout(() => {
                setCurrentIndex(0);
                setCarrots([]);
                startGame(currentSeq);
              }, 1000);
              return [];
            }
          }
        }

        // 4. SPAWN NEW CARROTS (using pre-generated queue - NO filtering/searching!)
        const activeCarrots = updatedCarrots.filter(c => now >= c.spawnTime);
        const totalCarrots = updatedCarrots.length;
        const timeSinceLastSpawn = now - lastSpawnTimeRef.current;
        const MIN_SPAWN_INTERVAL = 1000;
        
        if (timeSinceLastSpawn >= MIN_SPAWN_INTERVAL && totalCarrots < MAX_CARROTS_ON_SCREEN) {
          const spawnQueue = spawnQueueRef.current;
          const spawnIndex = spawnQueueIndexRef.current;
          const currentSeq = sequenceRef.current;
          const currentIdx = currentIndexRef.current;
          
          // CRITICAL: Check if the next needed word is currently on screen
          const nextNeededWord = currentSeq[currentIdx];
          const nextWordOnScreen = nextNeededWord ? updatedCarrots.some(c => c.sequenceId === nextNeededWord.id) : false;
          
          let nextSpawnItem;
          
          // PRIORITY: If next needed word is NOT on screen, spawn it immediately
          if (nextNeededWord && !nextWordOnScreen) {
            nextSpawnItem = nextNeededWord;
            correctWordSpawnCountRef.current = 0;
            console.log('[Game] Next word missing from screen, spawning:', nextSpawnItem.text);
          }
          // Otherwise use normal spawn logic
          else if (spawnIndex < spawnQueue.length) {
            // Use pre-generated queue
            nextSpawnItem = spawnQueue[spawnIndex];
            spawnQueueIndexRef.current = spawnIndex + 1;
            
            // Track if we spawned the correct word
            if (nextSpawnItem.id === nextNeededWord?.id) {
              correctWordSpawnCountRef.current = 0;
            } else {
              correctWordSpawnCountRef.current++;
            }
          } else {
            // Queue exhausted! Continue spawning with variety
            const spawnsSinceCorrect = correctWordSpawnCountRef.current;
            
            if (spawnsSinceCorrect >= 2 || currentIdx >= currentSeq.length) {
              // Time to spawn the next correct word (or we're done)
              if (currentIdx < currentSeq.length) {
                nextSpawnItem = currentSeq[currentIdx];
                correctWordSpawnCountRef.current = 0;
              } else {
                return updatedCarrots; // Game should be complete
              }
            } else {
              // Spawn a random word for variety
              const hasEarlierDuplicate = (item, maxIndex) => {
                for (let i = 0; i < maxIndex; i++) {
                  if (currentSeq[i].text === item.text && currentSeq[i].id !== item.id) {
                    return true;
                  }
                }
                return false;
              };
              
              let attempts = 0;
              do {
                nextSpawnItem = currentSeq[Math.floor(Math.random() * currentSeq.length)];
                attempts++;
                
                const randomItemIndex = currentSeq.findIndex(item => item.id === nextSpawnItem.id);
                
                if (randomItemIndex >= currentIdx) {
                  // Check if there's a duplicate ANYWHERE before this item's position
                  if (!hasEarlierDuplicate(nextSpawnItem, randomItemIndex)) {
                    break;
                  }
                } else {
                  if (!hasEarlierDuplicate(nextSpawnItem, randomItemIndex)) {
                    break;
                  }
                }
                
                if (attempts > 10) {
                  nextSpawnItem = currentSeq[currentIdx];
                  break;
                }
              } while (attempts < 10);
              
              correctWordSpawnCountRef.current++;
            }
          }
          
          if (nextSpawnItem) {
            updatedCarrots.push(createCarrot(nextSpawnItem, 0, activeCarrots));
            lastSpawnTimeRef.current = now;
          }
        }

        return updatedCarrots;
      });
      
      animationId = requestAnimationFrame(gameLoop);
    };
    
    animationId = requestAnimationFrame(gameLoop);
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [gameState]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || gameState !== 'playing') return;

    const ctx = canvas.getContext('2d');
    
    const draw = () => {
      ctx.fillStyle = '#87CEEB';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Draw ground
      ctx.fillStyle = '#8B4513';
      ctx.fillRect(0, CANVAS_HEIGHT - 80, CANVAS_WIDTH, 80);
      
      // Draw caught words in the ground area
      ctx.fillStyle = '#FFF';
      ctx.font = 'bold 11px Arial';
      ctx.textAlign = 'left';
      const wordsText = caughtWords.join(' ');
      const maxWidth = CANVAS_WIDTH - 20;
      
      // Word wrap the caught words
      const words = wordsText.split(' ');
      let line = '';
      let yPos = CANVAS_HEIGHT - 65;
      const lineHeight = 14;
      
      for (let i = 0; i < words.length; i++) {
        const testLine = line + words[i] + ' ';
        const metrics = ctx.measureText(testLine);
        
        if (metrics.width > maxWidth && i > 0) {
          ctx.fillText(line, 10, yPos);
          line = words[i] + ' ';
          yPos += lineHeight;
          
          if (yPos > CANVAS_HEIGHT - 10) break; // Stop if out of ground space
        } else {
          line = testLine;
        }
      }
      ctx.fillText(line, 10, yPos);

      // Draw carrots using pre-loaded image
      const carrotImg = carrotImageRef.current;
      if (carrotImg) {
        carrots.forEach(carrot => {
          // Skip if not yet spawned or already off-screen
          if (Date.now() < carrot.spawnTime || carrot.y > CANVAS_HEIGHT) return;
          
          // Draw carrot image
          ctx.drawImage(carrotImg, carrot.x, carrot.y, carrot.width, carrot.height);
          
          // Draw text in BLACK, LEFT-ALIGNED on the carrot
          ctx.fillStyle = '#000000';
          ctx.font = 'bold 12px Arial'; // Slightly bigger font for readability
          ctx.textAlign = 'left';
          
          // Word wrap text if too long
          const words = carrot.text.split(' ');
          const leftEdge = carrot.x + 8; // 8px padding from left edge
          
          if (words.length > 1) {
            ctx.fillText(words[0], leftEdge, carrot.y + carrot.height / 2 + 2); // Moved down by 7px
            ctx.fillText(words.slice(1).join(' '), leftEdge, carrot.y + carrot.height / 2 + 17); // Moved down by 7px
          } else {
            ctx.fillText(carrot.text, leftEdge, carrot.y + carrot.height / 2 + 9); // Moved down by 7px
          }
        });
      }

      // Draw basket using pre-loaded image (using ref for smooth movement)
      const basket = basketRef.current;
      const basketImg = basketImageRef.current;
      if (basketImg) {
        ctx.drawImage(basketImg, basket.x, basket.y, basket.width, basket.height);
      }

      // Draw game stats (NO "Next:" display - that would give it away!)
      ctx.fillStyle = '#000';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(`Mistakes: ${mistakes}`, 10, 25);
      ctx.fillText(`Progress: ${currentIndexRef.current}/${sequenceRef.current.length}`, 10, 45);
    };

    let animationId;
    const animate = () => {
      draw();
      if (gameState === 'playing') {
        animationId = requestAnimationFrame(animate);
      }
    };
    
    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [carrots, gameState]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || gameState !== 'playing') return;

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = CANVAS_WIDTH / rect.width;
      const x = (e.clientX - rect.left) * scaleX;
      
      const basket = basketRef.current;
      const newX = Math.max(0, Math.min(x - basket.width / 2, CANVAS_WIDTH - basket.width));
      
      // Update ref directly for immediate, smooth movement
      basketRef.current.x = newX;
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
    };
  }, [gameState, CANVAS_WIDTH]);

  // Collision detection is now handled in the consolidated game loop above

  const restartVerse = () => {
    setCarrots([]);
    setCurrentIndex(0);
    setMistakes(0);
    setScore(0);
    setCaughtWords([]);
    lastSpawnTimeRef.current = 0;
    spawnQueueRef.current = [];
    spawnQueueIndexRef.current = 0;
    setGameState('loading');
    loadVerse(verseId);
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader className="relative">
        <CardTitle>🥕 Test Mini-Game (Admin)</CardTitle>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
          aria-label="Close"
        >
          ×
        </button>
      </CardHeader>
      <CardContent>
        {gameState === 'loading' && (
          <div className="text-center py-12">
            <p className="text-lg">Loading test verse...</p>
          </div>
        )}

        {gameState === 'playing' && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 p-3 rounded">
              <p><strong>TEST MODE</strong></p>
              <p><strong>Current verse:</strong> {verse?.reference}</p>
              <p className="text-xs mt-1">Move mouse to control basket. Catch words in order!</p>
            </div>
            <canvas
              ref={canvasRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              className="border-4 border-gray-800 rounded-lg mx-auto block cursor-crosshair"
              style={{ touchAction: 'none', width: '100%', maxWidth: '375px' }}
            />
          </div>
        )}

        {gameState === 'won' && (
          <div className="text-center py-12 space-y-4">
            <h2 className="text-3xl font-bold text-green-600">✅ Test Complete!</h2>
            <p className="text-lg">
              {mistakes === 0 ? '🏆 Perfect!' : `Mistakes: ${mistakes}`}
            </p>
            <p>Final Score: {score}</p>
            <p className="text-sm text-gray-600">{verse?.reference}</p>
            <div className="flex gap-4 justify-center mt-4">
              <Button onClick={restartVerse}>Retry This Verse</Button>
              <Button onClick={onClose} variant="outline">Close</Button>
            </div>
          </div>
        )}

        {gameState === 'error' && (
          <div className="text-center py-12 space-y-4">
            <h2 className="text-2xl font-bold text-red-600">Error Loading Verse</h2>
            <Button onClick={onClose} variant="outline">Close</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CarrotCatchGameTest;
